import { computed } from 'vue'
import { DateTime } from 'luxon'
import { useNow } from '@vueuse/core'

// The opening hours are Dutch office hours, whatever the visitor's timezone
const ZONE = 'Europe/Amsterdam'

/**
 * Whether customer service can be reached by phone right now, and when it can be otherwise.
 *
 * `openingHours` is the `opening-hours` DataItem:
 * { days: { monday: { from: '09:00', to: '17:00', label: 'Maandag' } | { closed: true } }, excludedDates: ['2026-12-25'] }
 */
export function useReachability (openingHours) {
    const now = useNow({ interval: 60_000 })

    function hoursOn (date) {
        if (openingHours?.excludedDates?.includes(date.toISODate())) {
            return null
        }

        const day = openingHours?.days?.[date.setLocale('en').toFormat('cccc').toLowerCase()]

        return day && !day.closed && day.from && day.to ? day : null
    }

    function at (date, time) {
        const [hour, minute] = time.split(':').map(Number)
        return date.set({ hour, minute, second: 0, millisecond: 0 })
    }

    return computed(() => {
        const current = DateTime.fromJSDate(now.value).setZone(ZONE)
        const today = hoursOn(current)

        if (today && current >= at(current, today.from) && current < at(current, today.to)) {
            return { open: true, label: `Nu bereikbaar tot ${today.to}` }
        }

        if (today && current < at(current, today.from)) {
            return { open: false, label: `Vandaag bereikbaar vanaf ${today.from}` }
        }

        // Look ahead for the next working day, skipping weekends and holidays
        for (let days = 1; days <= 14; days++) {
            const hours = hoursOn(current.plus({ days }))

            if (hours) {
                return { open: false, label: `${days === 1 ? 'Morgen' : hours.label} weer bereikbaar vanaf ${hours.from}` }
            }
        }

        return { open: false, label: 'Momenteel telefonisch niet bereikbaar' }
    })
}
